import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../Logo';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useGlobalHighlight } from '@/hooks/useGlobalHighlight';
import '@/styles/navbar.css';

interface IrreplacableNavbarProps {
  onGetStarted: () => void;
}

export default function IrreplacableNavbar({ onGetStarted }: IrreplacableNavbarProps) {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { setNeedsHighlight } = useGlobalHighlight();
  
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  useClickOutside(mobileMenuRef, () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, toggleButtonRef);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    setIsMobileMenuOpen(false);
    onGetStarted();
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={`navbar ${isScrolled ? 'pt-2' : 'pt-4'}`}
    >
      <div className={`mx-auto max-w-7xl bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/80 transition-all duration-300 nav-animation ${
        isScrolled ? 'py-2' : 'py-4'
      }`}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0 transition-all duration-300">
              <Logo 
                className={`transition-all duration-300 ${
                  isScrolled ? 'h-10 md:h-10' : 'h-12 md:h-14'
                }`} 
                isHomepage={true} 
              />
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                Features
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full hidden md:block" />
              </a>
              <a href="#how-it-works" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                How it Works
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full hidden md:block" />
              </a>
              <a href="#example-report-section" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                View Example Report
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full hidden md:block" />
              </a>
              <a href="#pricing" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                Pricing
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full hidden md:block" />
              </a>
              
              <div className="hidden md:flex items-center">
                <button
                  onClick={handleGetStarted}
                  className="bg-primary text-dark px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors duration-200 font-medium"
                >
                  Start My Free Journey
                </button>
              </div>
            </div>

            <button
              ref={toggleButtonRef}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 focus:outline-none transition-colors duration-200"
              aria-label="Toggle navigation menu"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <div ref={mobileMenuRef} className="mobile-menu">
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.645, 0.045, 0.355, 1.000] }}
                className="md:hidden overflow-hidden"
              >
                <div className="px-4 pt-2 pb-6">
                  <div className="flex flex-col space-y-1 mb-6">
                    <a href="#features" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                      Features
                    </a>
                    <a href="#how-it-works" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                      How it Works
                    </a>
                    <a href="#example-report-section" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                      View Example Report
                    </a>
                    <a href="#pricing" className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 group md:text-left text-center py-3 md:py-0">
                      Pricing
                    </a>
                  </div>

                  <div className="flex flex-col space-y-3 border-t border-gray-200 pt-6">
                    <button
                      onClick={handleGetStarted}
                      className="w-full bg-primary text-dark px-6 py-3 rounded-lg hover:bg-primary-hover transition-colors duration-200 font-medium"
                    >
                      Start My Free Journey →
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}